import React, { useState, useEffect } from "react";
import api from "../api/homeapi";
import { StaffFrame, PageHead, CLASS_MAP, T } from "./DashboardShared";
import { formatDateFR } from "../utils/format";
import { useNotify, errorMessage } from "../components/Notify";

const CLASS_COLORS = { ECONOMIQUE: "#1D4F91", PREMIERE: "#6B21A8", VIP: "#B45309", DEUXIEME: "#0F766E" };

const css = `
.rev-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(190px,100%),1fr));gap:14px;margin-bottom:22px;}
.rev-kpi{background:${T.white};border:1.5px solid ${T.creamDk};border-radius:16px;padding:18px;}
.rev-kpi.total{background:linear-gradient(135deg,${T.forestDk},${T.forest});border-color:transparent;color:#fff;}
.rev-kpi-l{font-size:10.5px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:${T.smoke};margin-bottom:8px;}
.rev-kpi.total .rev-kpi-l{color:rgba(255,255,255,.55);}
.rev-kpi-v{font-family:'Playfair Display',serif;font-weight:900;font-size:clamp(20px,2.6vw,26px);line-height:1.1;}
.rev-kpi.total .rev-kpi-v{color:${T.goldLt};font-size:clamp(24px,3.4vw,32px);}
.rev-row{display:grid;grid-template-columns:110px 1fr 140px;align-items:center;gap:12px;padding:6px 0;}
.rev-date{font-size:12.5px;color:${T.smoke};font-family:'DM Mono',monospace;}
.rev-track{background:#f1ece0;border-radius:7px;height:24px;overflow:hidden;}
.rev-fill{height:100%;border-radius:7px;background:linear-gradient(90deg,${T.forest},${T.forestMid});display:flex;align-items:center;padding-left:8px;min-width:42px;}
.rev-fill span{font-size:11px;color:#fff;font-weight:700;}
.rev-amt{text-align:right;font-weight:700;font-size:13.5px;white-space:nowrap;}
@media(max-width:600px){
  .rev-row{grid-template-columns:1fr auto;grid-template-areas:"date amt" "bar bar";gap:6px 10px;padding:8px 0;border-bottom:1px dashed ${T.creamDk};}
  .rev-row:last-child{border-bottom:none;}
  .rev-date{grid-area:date;}.rev-amt{grid-area:amt;}.rev-track{grid-area:bar;}
}
`;

export default function AdminRevenue() {
  const notify = useNotify();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/admin/revenue-chart")
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(e => notify.error(errorMessage(e, "Impossible de charger les recettes.")))
      .finally(() => setLoading(false));
  }, [notify]);

  const byDate = data.reduce((acc, d) => { acc[d.date] = (acc[d.date] || 0) + Number(d.amount || 0); return acc; }, {});
  const totalRevenue = data.reduce((s, d) => s + Number(d.amount || 0), 0);
  const byClass = data.reduce((acc, d) => { if (d.class) acc[d.class] = (acc[d.class] || 0) + Number(d.amount || 0); return acc; }, {});
  const maxDay = Math.max(...Object.values(byDate), 1);

  return (
    <StaffFrame>
      <style>{css}</style>
      <div className="page medium">
        <PageHead title="Recettes SETRAG" sub="Suivi financier des ventes de billets" />

        {loading ? (
          <div className="loading-block"><div className="spinner"/>Chargement…</div>
        ) : (
          <>
            <div className="rev-kpis">
              <div className="rev-kpi total">
                <div className="rev-kpi-l">Recettes totales</div>
                <div className="rev-kpi-v">{totalRevenue.toLocaleString("fr-FR")} FCFA</div>
              </div>
              {Object.entries(byClass).map(([cls, amount]) => (
                <div key={cls} className="rev-kpi">
                  <div className="rev-kpi-l">{CLASS_MAP[cls]?.label || cls}</div>
                  <div className="rev-kpi-v" style={{ color: CLASS_COLORS[cls] || T.forest }}>{amount.toLocaleString("fr-FR")} FCFA</div>
                </div>
              ))}
            </div>

            <div className="stf-card">
              <div className="stf-card-title">Recettes par jour</div>
              <div className="stf-card-sub">Part de chaque journée dans le total de la période.</div>
              {Object.keys(byDate).length === 0 ? (
                <div className="empty" style={{ border: "none" }}><p className="empty-text">Aucune donnée disponible pour le moment.</p></div>
              ) : (
                Object.entries(byDate).sort(([a], [b]) => b.localeCompare(a)).map(([date, amount]) => {
                  const pct = totalRevenue ? Math.round((amount / totalRevenue) * 100) : 0;
                  return (
                    <div key={date} className="rev-row">
                      <div className="rev-date">{formatDateFR(date)}</div>
                      <div className="rev-track"><div className="rev-fill" style={{ width: `${Math.max((amount / maxDay) * 100, 4)}%` }}><span>{pct}%</span></div></div>
                      <div className="rev-amt">{amount.toLocaleString("fr-FR")} FCFA</div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </StaffFrame>
  );
}
